/**
 * SigTDD Application Root
 *
 * Routed by pathname (framing V2/V9): public pages are real URLs that the
 * build prerenders and the client hydrates; the workspace and auth pages
 * are client-only. Wrapped in ThemeProvider + PlanProvider for global state.
 *
 * Server-render safety: nothing here touches `window` during render. The
 * current path comes from `useLocationPath()`, which reads the server path
 * set by entry-server.tsx during prerender.
 */

import { useEffect, useState, useRef, useCallback, lazy, Suspense } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { PlanProvider } from './context/PlanContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AuthPromptContext, type AuthIntent } from './context/auth-prompt';
import { ToastProvider } from './components/Toast';
import Navbar from './components/Navbar';
import SiteFooter from './components/SiteFooter';
import NotFoundPage from './components/NotFoundPage';
import AccountPrompt from './components/AccountPrompt';
import VerificationBanner from './components/auth/VerificationBanner';
import { track } from './lib/analytics';
import { matchRoute, type PublicRoute } from './lib/route';
import { navigate, useLocationPath } from './lib/router';
import { applyHead } from './lib/head';
import { LandingPage } from './components/landing';
import { DocsPage } from './components/docs';
import { ContactPage } from './components/contact';
import { PricingPage } from './components/pricing';
import { FeaturesPage } from './components/features';
import { LEARN_PAGE_BY_PATH } from './components/learn';
import { LoginPage, SignupPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage } from './components/auth';
import './App.css';

// Client-only heavy surfaces stay out of the public-page bundle (framing V13)
const Workspace = lazy(() => import('./components/workspace'));
const SharedPlanViewer = lazy(() => import('./components/SharedPlanViewer'));

function WorkspaceLoader() {
  return (
    <div className="app-content" style={{ alignItems: 'center', justifyContent: 'center', color: 'var(--text-dimmed)' }}>
      Loading workspace...
    </div>
  );
}

function PublicPage({ route, onSignup }: { route: PublicRoute; onSignup: () => void }) {
  switch (route.kind) {
    case 'landing':
      return <LandingPage onSignup={onSignup} />;
    case 'features':
      return <FeaturesPage onSignup={onSignup} />;
    case 'pricing':
      return <PricingPage onSignup={onSignup} />;
    case 'docs':
      return <DocsPage />;
    case 'contact':
      return <ContactPage />;
    case 'learn': {
      const Page = LEARN_PAGE_BY_PATH[route.path];
      return Page ? <Page route={route} /> : <NotFoundPage />;
    }
    default:
      return <NotFoundPage />;
  }
}

function AppContent() {
  const path = useLocationPath();
  const match = matchRoute(path);
  const { user, logout } = useAuth();

  // Account prompt (framing S-D4): an action waits until the user is signed in
  const [promptIntent, setPromptIntent] = useState<AuthIntent | null>(null);
  const pendingAction = useRef<(() => void) | null>(null);
  const requestAuth = useCallback((intent: AuthIntent, onAuthed: () => void) => {
    pendingAction.current = onAuthed;
    setPromptIntent(intent);
    track('save-prompt-open', { source: intent });
  }, []);

  // Keep title, description and canonical current on client-side navigation
  useEffect(() => {
    applyHead(match);
  }, [match]);

  // Escape returns to the workspace from any page (behaviour kept from the single-document app)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (match.kind === 'app' && match.page === 'app') return;
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      navigate('/app');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [match]);

  const goApp = () => navigate('/app');

  // After successful login/signup, return to the workspace and finish the action that asked for an account
  const handleAuthSuccess = () => {
    navigate('/app');
    const action = pendingAction.current;
    pendingAction.current = null;
    if (action) setTimeout(action, 0);
  };

  // Every entry to the signup form records where it came from (framing S-D6)
  const openSignup = (source: string) => {
    track('signup-start', { source });
    navigate('/signup');
  };

  const renderPage = () => {
    if (match.kind === 'public') {
      const source = match.route.kind === 'landing' ? 'landing' : match.route.kind;
      return (
        <div className="page-container">
          <PublicPage route={match.route} onSignup={() => openSignup(source)} />
          <SiteFooter />
        </div>
      );
    }
    if (match.kind === 'not-found') {
      return (
        <div className="page-container">
          <NotFoundPage />
          <SiteFooter />
        </div>
      );
    }
    if (match.kind !== 'app') return null;
    switch (match.page) {
      case 'login':
        return (
          <div className="page-container">
            <LoginPage
              onClose={goApp}
              onSignup={() => openSignup('login')}
              onSuccess={handleAuthSuccess}
              onForgotPassword={() => navigate('/forgot-password')}
            />
          </div>
        );
      case 'signup':
        return (
          <div className="page-container">
            <SignupPage
              onClose={goApp}
              onLogin={() => navigate('/login')}
              onSuccess={handleAuthSuccess}
            />
          </div>
        );
      case 'verify-email':
        return (
          <div className="page-container">
            <VerifyEmailPage onClose={goApp} onLogin={() => navigate('/login')} />
          </div>
        );
      case 'forgot-password':
        return (
          <div className="page-container">
            <ForgotPasswordPage onClose={goApp} onLogin={() => navigate('/login')} />
          </div>
        );
      case 'reset-password':
        return (
          <div className="page-container">
            <ResetPasswordPage onClose={goApp} onLogin={() => navigate('/login')} />
          </div>
        );
      default:
        return (
          <Suspense fallback={<WorkspaceLoader />}>
            <Workspace />
          </Suspense>
        );
    }
  };

  return (
    <AuthPromptContext.Provider value={{ requestAuth }}>
      <div className="app">
        <Navbar
          user={user}
          path={path}
          onOpenLogin={() => navigate('/login')}
          onOpenSignup={() => openSignup('navbar')}
          onLogout={logout}
        />
        <VerificationBanner onOpenContact={() => navigate('/contact')} />
        {renderPage()}
        {promptIntent && (
          <AccountPrompt
            intent={promptIntent}
            onCancel={() => { setPromptIntent(null); pendingAction.current = null; }}
            onLogin={() => { setPromptIntent(null); navigate('/login'); }}
            onSignup={() => { setPromptIntent(null); openSignup(`${promptIntent}-prompt`); }}
          />
        )}
      </div>
    </AuthPromptContext.Provider>
  );
}

export default function App() {
  const path = useLocationPath();
  const match = matchRoute(path);

  // A shared plan renders a simplified view (no auth, no main PlanProvider)
  if (match.kind === 'share') {
    return (
      <ThemeProvider>
        <ToastProvider>
          <Suspense fallback={<WorkspaceLoader />}>
            <SharedPlanViewer shareToken={match.token} onClose={() => navigate('/app', { replace: true })} />
          </Suspense>
        </ToastProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <PlanProvider>
            <AppContent />
          </PlanProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
